import { call, put, takeEvery, select, delay } from 'redux-saga/effects';
import { NOTIFY_LEVEL, RequestStatus, SHOW_NOTIFY } from 'constants';
import {
  GET_REACTIONS,
  CREATE_REACTION,
  UPDATE_REACTION,
  DELETE_REACTION,
  MARK_AS_READ_REACTION,
  SET_REACTIONS_REQ_STATUS,
  WS_CREATE_REACTION,
  WS_UPDATE_REACTION,
  WS_DELETE_REACTION,
  CLEAR_REACTIONS,
} from '@control-front-end/common/constants/reactions';
import {
  UPDATE_ACTOR,
  UPDATE_ACTOR_VIEW,
  WS_CREATE_ATTACHMENTS,
  WS_DELETE_ATTACHMENTS,
} from '@control-front-end/common/constants/graphActors';
import api from '@control-front-end/common/sagas/api';
import {
  makeAttach,
  bindAttach,
  unBindAttach,
} from '@control-front-end/common/sagas/attachUtils';
import AppUtils from '@control-front-end/utils/utils';
import { getActiveLayer, updateActorProp } from './graph/graphHelpers';

// Формируем массив индексов реакций одинакового типа
function makeGroupReactionsIndexes(list, groupTypes) {
  const groupList = [];
  let obj = null;
  const listLen = list.length;
  list.forEach((i, index) => {
    const type = i.data.type;
    if (!obj) {
      obj = { start: index, end: index + 1, type };
    } else if (obj.type !== type) {
      groupList.push(obj);
      obj = { start: index, end: index + 1, type };
    } else if (!i.children.length) {
      obj.end = index + 1;
    }
    if ((listLen - 1 === index && obj) || i.children.length) {
      groupList.push(obj);
      obj = null;
    }
  });
  return groupList.filter((i) => groupTypes.includes(i.type));
}

// Добавляем группирующую реакцию
function makeReactionGroups(list) {
  const groups = makeGroupReactionsIndexes(list, ['view']);
  for (const g of groups) {
    const viewsR = list.slice(g.start, g.end);
    if (viewsR.length < 2) continue;
    viewsR.forEach((i) => {
      i.groupedItem = true;
    });
    const groupReaction = {
      id: `${g.start}_${g.end}`,
      isGroup: true,
      title: g.type,
      children: viewsR,
      treeInfo: {},
    };
    list.splice(g.start, g.end - g.start, groupReaction);
  }
  return list;
}

// Преобразовать список реакций в дерево
function* unflattenList(list) {
  const mapIndex = {};
  const fullMap = {};
  const roots = [];
  const config = yield select((state) => state.config);
  for (let i = 0, l = list.length; i < l; i += 1) {
    const reaction = list[i];
    mapIndex[reaction.id] = i;
    fullMap[reaction.id] = reaction;
    reaction.children = reaction.children || [];
    if (reaction.user) {
      reaction.user.userAvatar = config.avatarPath.replace(
        '{userId}',
        reaction.userId
      );
      if (reaction.userId === 0)
        reaction.user.userAvatar = config.systemUserAvatar;
    }
  }
  for (let i = 0, l = list.length; i < l; i += 1) {
    const reaction = list[i];
    if (reaction.treeInfo.parentId) {
      const parentReaction = fullMap[reaction.treeInfo.parentId];
      reaction.parentUserName = parentReaction.user.nick;
      list[mapIndex[reaction.treeInfo.parentId]].children.push(reaction);
    } else {
      roots.push(reaction);
    }
  }
  makeReactionGroups(roots);
  return roots;
}

// Установить комментарий в нужное место в дереве
function setChildReaction(rootReaction, data) {
  if (rootReaction.id === data.treeInfo.parentId) {
    data.parentUserName = rootReaction.user.nick;
    rootReaction.children.push(data);
    return;
  }
  for (const child of rootReaction.children) {
    if (child.id === data.parentId) {
      data.parentUserName = child.user.nick;
      child.children.push(data);
      break;
    } else {
      setChildReaction(child, data);
    }
  }
}

// Обновить реакцию в дереве
function updateChildReaction(rootReaction, data) {
  for (const child of rootReaction.children) {
    if (child.id === data.id) {
      child.extra = data.extra;
      child.attachments = data.attachments;
      child.updatedAt = data.updated_at;
      break;
    } else {
      updateChildReaction(child, data);
    }
  }
}

// Проверка на то, что комментарий не надо добавлять в стрим
function* notAddedCommet(r) {
  const reactions = yield select((state) => state.reactions);
  const { treeInfo } = r;
  return (
    reactions.orderValue === 'ASC' && !treeInfo.parentId && !reactions.endList
  );
}

// Удалить реакцию из дерева
function deleteChildReaction(rootReaction, data) {
  if (rootReaction.id === data.parentId) {
    const index = rootReaction.children.findIndex((i) => i.id === data.id);
    rootReaction.children.splice(index, 1);
    return;
  }
  for (const child of rootReaction.children) {
    if (child.id === data.parentId) {
      const index = child.children.findIndex((i) => i.id === data.id);
      child.children.splice(index, 1);
      break;
    } else {
      deleteChildReaction(child, data);
    }
  }
}

// Нотификация, что реакция добавлена
function* showNotifyAdded() {
  const reactions = yield select((state) => state.reactions);
  yield put({
    type: SHOW_NOTIFY.REQUEST,
    payload: {
      id: AppUtils.createRid(),
      type: NOTIFY_LEVEL.SUCCESS,
      label: 'Сomment has been added to the end of the list',
    },
  });
  yield put({
    type: CREATE_REACTION.SUCCESS,
    payload: { total: reactions.total + 1 },
  });
}

// Обновить счетчик коммендариев для зоны на слое
function* updateLayerAreaCommentsCount({ actorId }) {
  const activeLayer = yield getActiveLayer();
  if (!activeLayer) return;
  const { list: reactionsList } = yield select((state) => state.reactions);
  const commentsCount = reactionsList.filter(
    (i) => i.data.type === 'comment'
  ).length;
  yield updateActorProp({
    type: UPDATE_ACTOR.SUCCESS,
    layerId: activeLayer.id,
    actorId,
    propId: 'commentsCount',
    value: commentsCount,
  });
}

// Актуализировать статистику реакций по актору
function* updateActorViewReactionStat(reaction, action) {
  const {
    userId,
    data: { type },
  } = reaction;
  const actorView = yield select((state) => state.actorView);
  const newStats = actorView.reactionsStats
    ? structuredClone(actorView.reactionsStats)
    : {};
  if (action === 'add') {
    newStats[userId] =
      newStats && newStats[userId]
        ? {
            ...newStats[userId],
            [type]: newStats[userId][type] ? newStats[userId][type] + 1 : 1,
          }
        : { [type]: 1 };
  } else if (newStats[userId] && newStats[userId][type]) {
    newStats[userId][type] -= 1;
  }
  yield put({
    type: UPDATE_ACTOR_VIEW.SUCCESS,
    payload: { ...actorView, reactionsStats: newStats },
  });
  const isLayerArea = actorView.data && actorView.data.channel === 'layerArea';
  if (isLayerArea) {
    yield updateLayerAreaCommentsCount({ actorId: actorView.id });
  }
}

// Добавить реакцию
function* addReaction(r) {
  const reactions = yield select((state) => state.reactions);
  const list = structuredClone(reactions.list);
  const addFunc = reactions.orderValue === 'ASC' ? 'push' : 'unshift';
  if (!r.treeInfo.parentId) {
    list[addFunc]({ ...r });
  } else {
    const rootReaction = list.find((i) => i.id === r.treeInfo.branchId);
    setChildReaction(rootReaction, r);
  }
  yield put({
    type: CREATE_REACTION.SUCCESS,
    payload: {
      list,
      total: reactions.total + 1,
      offset: reactions.offset + 1,
    },
  });
  yield updateActorViewReactionStat(r, 'add');
}

// Создать модель реакции
function* createReactionModel(r, actorView, attachments) {
  const config = yield select((state) => state.config);
  const reactions = yield select((state) => state.reactions);
  // Если стрим реакций открыт по ссылке на реакцию, то
  // делаем запрос на получение реакций заново
  if (reactions.minOffset >= 0) {
    yield put({ type: CLEAR_REACTIONS });
    yield put({
      type: GET_REACTIONS.REQUEST,
      payload: { actorId: actorView.id, loadMore: false },
    });
    return;
  }
  r.children = [];
  r.user = r.user || {};
  r.user.userAvatar = config.avatarPath.replace('{userId}', r.userId);
  if (r.userId === 0) r.user.userAvatar = config.systemUserAvatar;
  r.attachments = attachments;
  if (yield notAddedCommet(r)) {
    yield showNotifyAdded();
  } else {
    yield addReaction(r);
  }
}

// Удалить модель реакции
function* removeReactionModel(reaction) {
  const copyReaction = { ...reaction };
  const { id, treeInfo = {} } = copyReaction;
  const { parentId, branchId } = treeInfo;
  const reactions = yield select((state) => state.reactions);
  const reactionsList = structuredClone(reactions.list);
  if (!parentId) {
    const index = reactionsList.findIndex((i) => i.id === id);
    if (index === -1) return;
    yield updateActorViewReactionStat(reactionsList[index], 'delete');
    reactionsList.splice(index, 1);
  } else {
    const rootReaction = reactionsList.find((i) => i.id === branchId);
    deleteChildReaction(rootReaction, { id, parentId });
  }
  yield put({
    type: DELETE_REACTION.SUCCESS,
    payload: {
      list: reactionsList,
      total: reactions.total - 1,
      offset: reactions.offset - 1,
    },
  });
}

// Обновить модель реакции
function* updateReactionModel(originReaction, attachments) {
  const reaction = structuredClone(originReaction);
  const config = yield select((state) => state.config);
  const reactions = yield select((state) => state.reactions);
  const reactionsList = structuredClone(reactions.list);
  const { id, treeInfo } = reaction;
  const { parentId, branchId } = treeInfo;
  reaction.user = reaction.user || {};
  reaction.user.userAvatar = config.avatarPath.replace(
    '{userId}',
    reaction.userId
  );
  if (reaction.userId === 0) reaction.user.userAvatar = config.systemUserAvatar;
  if (attachments) reaction.attachments = attachments;
  if (!parentId) {
    const index = reactionsList.findIndex((i) => i.id === id);
    if (index !== -1) {
      const findReaction = reactionsList[index];
      reactionsList.splice(index, 1, { ...findReaction, ...reaction });
    }
  } else {
    const rootReaction = reactionsList.find((i) => i.id === branchId);
    updateChildReaction(rootReaction, reaction);
  }
  yield put({
    type: UPDATE_REACTION.SUCCESS,
    payload: { list: reactionsList },
  });
}

// Проверить надо ли обрабатывать пакет из WS
function* isHandleWSPacket(reaction, tabId, checkIsModelExist = true) {
  const accounts = yield select((state) => state.accounts);
  const reactions = yield select((state) => state.reactions);
  const actorView = yield select((state) => state.actorView);
  const appAuth = yield select((state) => state.appAuth);
  if (tabId && appAuth.tabId === tabId) return false;
  const { id, accId, treeInfo } = reaction;
  if (accounts.active !== accId || actorView.id !== treeInfo.rootActorId)
    return false;
  // Поиск существующей реакции
  if (checkIsModelExist) {
    const reactionFromState = AppUtils.recursiveFind(reactions.list, id);
    if (reactionFromState) return false;
  }
  return true;
}

/**
 * Получить реакции
 */
function* getReactions({ payload, callback }) {
  const { actorId, rid, loadMore, loadPrev } = payload;
  const reactionsState = yield select((state) => state.reactions);
  const { reqStatus, list, limit, offset, minOffset, orderValue, endList } =
    reactionsState;
  if (reqStatus !== RequestStatus.SUCCESS || (endList && !loadPrev)) {
    return;
  }
  const getOffset = loadPrev ? minOffset : offset;
  yield put({ type: SET_REACTIONS_REQ_STATUS, payload: 'inProgress' });
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/reactions/list/${actorId}`,
    queryParams: { limit, offset: getOffset, rid, orderValue },
  });
  yield put({ type: SET_REACTIONS_REQ_STATUS, payload: 'success' });
  if (result !== RequestStatus.SUCCESS) return;
  const { total, list: listReactions } = data.data;
  const treeData = yield call(unflattenList, listReactions);
  let newList;
  if (loadPrev) {
    newList = treeData.concat(list);
    const prevOffset = data.data.offset - limit;
    yield put({
      type: GET_REACTIONS.SUCCESS,
      payload: {
        list: newList,
        minOffset: prevOffset,
      },
    });
  } else {
    newList = loadMore ? list.concat(treeData) : treeData;
    const nextOffset = data.data.offset + limit;
    const prevOffset = data.data.offset - limit;
    yield put({
      type: GET_REACTIONS.SUCCESS,
      payload: {
        list: newList,
        offset: nextOffset,
        minOffset: rid ? prevOffset : minOffset,
        orderValue,
        total,
        endList: nextOffset >= total,
      },
    });
  }

  if (callback) callback(newList);
}

/**
 * Создать реакцию
 */
function* createReaction({ payload, callback }) {
  const {
    rootActorId,
    description,
    files,
    formActions,
    type,
    parentId,
    appId,
    appSettings,
    data: formData,
  } = payload;

  const { result, data } = yield call(api, {
    method: 'post',
    url: `/reactions/${type}/${rootActorId}`,
    body: {
      description,
      parentId,
      appId,
      appSettings,
      data: formData,
    },
  });
  if (formActions) formActions.setSubmitting(false);
  if (result !== RequestStatus.SUCCESS) return;
  const actorView = yield select((state) => state.actorView);
  // создание аттачей
  const attachments = yield call(makeAttach, files);
  // Создать модель реакции
  yield call(createReactionModel, data.data, actorView, attachments);
  // привязка аттачей
  yield call(bindAttach, attachments, data.data.id);
  if (formActions) formActions.onClose();
  if (callback) callback(data.data);
}

/**
 * Обновить реакцию
 */
function* updateReaction({ payload }) {
  const {
    rootActorId,
    actorId,
    description,
    formActions,
    appId,
    appSettings,
    data: formData,
  } = payload;
  let { files } = payload;
  const reactions = yield select((state) => state.reactions);
  const reactionFromState = AppUtils.recursiveFind(reactions.list, actorId);
  // поиск аттачей для удаления
  const filesToDelete = [];
  for (const attach of reactionFromState.attachments) {
    if (!files.find((f) => f.id === attach.id)) {
      filesToDelete.push({ id: attach.id });
    }
  }
  // удаление аттачей
  if (filesToDelete.length) {
    yield call(unBindAttach, filesToDelete, reactionFromState.id);
    files = files.filter((i) => !filesToDelete.find((u) => u.id === i.id));
  }
  // создание аттачей
  const attachments = yield call(makeAttach, files);
  // привязка аттачей
  yield call(bindAttach, attachments, reactionFromState.id);
  const { result, data } = yield call(api, {
    method: 'put',
    url: `/reactions/${rootActorId}`,
    body: {
      actorId,
      description,
      appId: appId || reactionFromState.appId,
      appSettings: appSettings || reactionFromState.appSettings,
      data: formData,
    },
  });
  if (formActions) formActions.setSubmitting(false);
  if (result !== RequestStatus.SUCCESS) return;
  // Обновить модель реакции
  yield call(updateReactionModel, data.data, attachments);
  if (formActions) formActions.onClose();
}

/**
 * Удалить реакцию
 */
function* deleteReaction({ payload }) {
  const { rootActorId, id } = payload;
  const { result } = yield call(api, {
    method: 'delete',
    url: `/reactions/${rootActorId}`,
    body: { actorId: id },
  });
  if (result !== RequestStatus.SUCCESS) return;
  // Удалить реакцию
  yield call(removeReactionModel, { ...payload });
}

/**
 * Получить новую реакцию по WS
 */
function* wsCreateReaction({ payload: { model: reaction, tabId } }) {
  const isHandle = yield call(isHandleWSPacket, reaction, tabId, true);
  if (!isHandle) return;
  const actorView = yield select((state) => state.actorView);
  if (yield notAddedCommet(reaction)) return;
  yield call(createReactionModel, reaction, actorView, []);
}

/**
 * Удалить реакцию по WS
 */
function* wsDeleteReaction({ payload: { model: reaction, tabId } }) {
  const isHandle = yield call(isHandleWSPacket, reaction, tabId, false);
  if (!isHandle) return;
  yield call(removeReactionModel, reaction);
}

/**
 * Обновить реакцию по WS
 */
function* wsUpdateReaction({ payload: { model: reaction, tabId } }) {
  const isHandle = yield call(isHandleWSPacket, reaction, tabId, false);
  if (!isHandle) return;
  yield call(updateReactionModel, reaction, reaction.attachments || []);
}

/**
 * Добавить вложения по WS
 */
function* wsCreateAttachments({ payload }) {
  yield delay(1000);
  const reactions = yield select((state) => state.reactions);
  const appAuth = yield select((state) => state.appAuth);
  const { attachments, tabId } = payload;
  if (tabId && appAuth.tabId === tabId) return;
  // Добавить вложение в реакцию
  const reactionModel = AppUtils.recursiveFind(
    reactions.list,
    attachments[0].actorId
  );
  if (!reactionModel) return;
  yield call(updateReactionModel, { ...reactionModel }, attachments);
}

/**
 * Удалить вложения по WS
 */
function* wsDeleteAttachments({ payload }) {
  const reactions = yield select((state) => state.reactions);
  const appAuth = yield select((state) => state.appAuth);
  const { attachments, tabId } = payload;
  if (tabId && appAuth.tabId === tabId) return;
  const removedIds = attachments.map((i) => i.id);
  // Удалить вложение из реакции
  const reactionModel = AppUtils.recursiveFind(
    reactions.list,
    attachments[0].actorId
  );
  if (!reactionModel) return;
  const filterAttachments = reactionModel.attachments.filter(
    (i) => !removedIds.includes(i.id)
  );
  yield call(updateReactionModel, { ...reactionModel }, filterAttachments);
}

/**
 * Отметить реакцию как прочитанную
 */
function* markAdReadReaction({ payload: id }) {
  // Задержка для анимации
  yield delay(1000);
  const reactions = yield select((state) => state.reactions);
  const reactionsList = structuredClone(reactions.list);
  const reactionModel = AppUtils.recursiveFind(reactionsList, id);
  if (!reactionModel) return;
  delete reactionModel.isNew;
  yield put({
    type: UPDATE_REACTION.SUCCESS,
    payload: { list: reactionsList },
  });
}

function* reactionsSaga() {
  yield takeEvery(GET_REACTIONS.REQUEST, getReactions);
  yield takeEvery(CREATE_REACTION.REQUEST, createReaction);
  yield takeEvery(DELETE_REACTION.REQUEST, deleteReaction);
  yield takeEvery(UPDATE_REACTION.REQUEST, updateReaction);
  yield takeEvery(MARK_AS_READ_REACTION.REQUEST, markAdReadReaction);
  yield takeEvery(WS_CREATE_REACTION, wsCreateReaction);
  yield takeEvery(WS_DELETE_REACTION, wsDeleteReaction);
  yield takeEvery(WS_UPDATE_REACTION, wsUpdateReaction);
  yield takeEvery(WS_CREATE_ATTACHMENTS, wsCreateAttachments);
  yield takeEvery(WS_DELETE_ATTACHMENTS, wsDeleteAttachments);
}

export default reactionsSaga;
